/* ===========================================================================
   Shady Co — theme behaviour
   ---------------------------------------------------------------------------
   Every inline <script> from the five HTML exports, deduplicated and made
   idempotent. Two things changed beyond consolidation:

     * Nothing assumes an element exists. The original scripts called
       `document.getElementById('site-header').classList` unconditionally, which
       threw on any page without that node. In a theme, sections come and go.
     * Everything re-initialises on `shopify:section:load`, which the theme
       editor fires whenever a merchant edits a section. Without this, animations
       and the gallery silently stop working while someone is designing.
   =========================================================================== */
(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------------
     Header: transparent over hero, emerald once scrolled.
     --------------------------------------------------------------------- */
  function initHeader() {
    var header = document.getElementById('site-header');
    if (!header || header.dataset.headerBound === 'true') return;
    header.dataset.headerBound = 'true';

    var isOverlay = header.dataset.overlay === 'true';

    function onScroll() {
      var scrolled = window.scrollY > 50;
      header.classList.toggle('is-solid', scrolled);
      header.classList.toggle('shadow-sm', scrolled);

      if (isOverlay) {
        header.classList.toggle('bg-transparent', !scrolled);
      } else {
        header.classList.toggle('text-night', !scrolled);
        header.classList.toggle('bg-surface', !scrolled);
      }
      header.classList.toggle('text-paper', scrolled || isOverlay);
    }

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }


  /* ---------------------------------------------------------------------
     Announcement bar

     The bar sits above a header that is fixed to the top of the window, so
     the header has to know how tall the bar actually is. Hard-coding a height
     breaks the moment a message wraps to two lines on a narrow phone, so the
     bar measures itself and publishes --announcement-h; the header and the
     spacer read that variable. ResizeObserver keeps it correct through
     rotation, rotation of the device, and font loading.
     --------------------------------------------------------------------- */
  function initAnnouncement() {
    var bar = document.getElementById('announcement-bar');
    var root = document.documentElement;

    if (!bar) {
      root.style.removeProperty('--announcement-h');
      document.body.style.removeProperty('padding-top');
      return;
    }

    function measure() {
      var height = bar.offsetHeight;
      root.style.setProperty('--announcement-h', height + 'px');
      // The bar is opaque and fixed, so every page needs that much clearance —
      // including the hero pages, where the header itself deliberately overlays.
      document.body.style.paddingTop = height + 'px';
    }

    measure();

    if ('ResizeObserver' in window) {
      new ResizeObserver(measure).observe(bar);
    } else {
      window.addEventListener('resize', measure);
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure);
    }

    if (bar.dataset.announcementBound === 'true') return;
    bar.dataset.announcementBound = 'true';

    var messages = bar.querySelectorAll('[data-announcement-message]');
    var interval = parseInt(bar.dataset.rotate, 10);
    if (messages.length < 2 || !interval || prefersReducedMotion) return;

    var index = 0;
    window.setInterval(function () {
      var current = messages[index];
      index = (index + 1) % messages.length;
      var next = messages[index];

      current.classList.add('opacity-0');
      window.setTimeout(function () {
        current.classList.add('hidden');
        next.classList.remove('hidden');
        window.requestAnimationFrame(function () {
          next.classList.remove('opacity-0');
          measure();
        });
      }, 500);
    }, interval);
  }

  /* ---------------------------------------------------------------------
     Mobile menu
     --------------------------------------------------------------------- */
  function initMobileMenu() {
    var btn = document.getElementById('mobile-menu-btn');
    var menu = document.getElementById('mobile-menu');
    var backdrop = document.getElementById('mobile-menu-backdrop');
    if (!btn || !menu || btn.dataset.menuBound === 'true') return;
    btn.dataset.menuBound = 'true';

    function toggle() {
      var expanded = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!expanded));
      btn.classList.toggle('active', !expanded);

      if (!expanded) {
        menu.classList.remove('hidden');
        if (backdrop) {
          backdrop.classList.remove('hidden');
          window.setTimeout(function () { backdrop.classList.remove('opacity-0'); }, 10);
        }
        document.body.style.overflow = 'hidden';
      } else {
        if (backdrop) backdrop.classList.add('opacity-0');
        window.setTimeout(function () {
          menu.classList.add('hidden');
          if (backdrop) backdrop.classList.add('hidden');
        }, 300);
        document.body.style.overflow = '';
      }
    }

    btn.addEventListener('click', toggle);
    if (backdrop) backdrop.addEventListener('click', toggle);

    menu.querySelectorAll('a').forEach(function (link) {
      link.addEventListener('click', function () {
        if (btn.getAttribute('aria-expanded') === 'true') toggle();
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && btn.getAttribute('aria-expanded') === 'true') toggle();
    });
  }

  /* ---------------------------------------------------------------------
     Scroll reveal
     --------------------------------------------------------------------- */
  function initReveal(root) {
    var scope = root || document;
    var elements = scope.querySelectorAll('.reveal:not(.active)');
    if (!elements.length) return;

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      elements.forEach(function (el) { el.classList.add('active'); });
      return;
    }

    var observer = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('active');
        obs.unobserve(entry.target);
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });

    elements.forEach(function (el) { observer.observe(el); });

    // Anything already in the first viewport reveals immediately, so the hero
    // is never blank on load.
    window.setTimeout(function () {
      scope.querySelectorAll('.hero .reveal, [data-reveal-immediate] .reveal').forEach(function (el) {
        el.classList.add('active');
      });
    }, 100);
  }

  /* ---------------------------------------------------------------------
     Product cards: hover-flip on desktop, scroll-flip on mobile
     --------------------------------------------------------------------- */
  function initCardFlip(root) {
    var scope = root || document;
    var cards = scope.querySelectorAll('.card-flip');
    if (!cards.length || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(function (entries) {
      if (window.innerWidth >= 768) return;
      entries.forEach(function (entry) {
        var inner = entry.target.querySelector('.card-flip-inner');
        if (!inner) return;
        inner.classList.toggle('is-flipped', entry.isIntersecting);
        entry.target.classList.toggle('mobile-ring-active', entry.isIntersecting);
      });
    }, { threshold: 1.0 });

    cards.forEach(function (card) { observer.observe(card); });

    window.addEventListener('resize', function () {
      if (window.innerWidth < 768) return;
      document.querySelectorAll('.card-flip-inner.is-flipped').forEach(function (el) {
        el.classList.remove('is-flipped');
      });
      document.querySelectorAll('.card-inner-ring.mobile-ring-active').forEach(function (el) {
        el.classList.remove('mobile-ring-active');
      });
    });
  }

  /* ---------------------------------------------------------------------
     Waitlist modal
     --------------------------------------------------------------------- */
  var WAITLIST_SEEN_KEY = 'shady:waitlist-seen';
  var WAITLIST_SIGNED_KEY = 'shady:waitlist-signed-up';

  // Two different memories, deliberately.
  //
  //   Dismissed  -> sessionStorage. Forgotten when the visitor closes the tab,
  //                 so someone who comes back another day is asked again. It
  //                 only stops the modal firing on every page of one visit.
  //   Signed up  -> localStorage. Permanent. They gave us their address; we do
  //                 not ask twice.
  //
  // Both fail open: if storage throws (private browsing, blocked cookies) we
  // would rather show the modal than break the page.
  function storeGet(store, key) {
    try { return window[store].getItem(key); } catch (e) { return null; }
  }
  function storeSet(store, key, value) {
    try { window[store].setItem(key, value); } catch (e) { /* fail open */ }
  }

  function hasSignedUp() {
    return Boolean(storeGet('localStorage', WAITLIST_SIGNED_KEY));
  }

  var waitlistLastFocus = null;

  function waitlistFocusable(modal) {
    return Array.prototype.filter.call(
      modal.querySelectorAll('a[href], button, input, [tabindex]:not([tabindex="-1"])'),
      function (el) { return !el.disabled && el.offsetParent !== null; }
    );
  }

  function openWaitlist() {
    var modal = document.getElementById('waitlist-modal');
    var content = document.getElementById('waitlist-content');
    if (!modal || !content) return;
    if (modal.dataset.open === 'true') return;

    waitlistLastFocus = document.activeElement;
    modal.dataset.open = 'true';

    modal.classList.remove('opacity-0', 'pointer-events-none');
    modal.setAttribute('aria-hidden', 'false');
    content.classList.remove('translate-y-8');
    content.classList.add('translate-y-0');

    var input = modal.querySelector('input[type="email"]');
    if (input) input.focus();

    if (modal.dataset.once === 'true') {
      storeSet('sessionStorage', WAITLIST_SEEN_KEY, '1');
    }
  }

  function closeWaitlist() {
    var modal = document.getElementById('waitlist-modal');
    var content = document.getElementById('waitlist-content');
    if (!modal || !content) return;

    modal.dataset.open = 'false';
    modal.classList.add('opacity-0', 'pointer-events-none');
    modal.setAttribute('aria-hidden', 'true');
    content.classList.remove('translate-y-0');
    content.classList.add('translate-y-8');

    // Send focus back where it came from, so keyboard users are not dumped at
    // the top of the document.
    if (waitlistLastFocus && typeof waitlistLastFocus.focus === 'function') {
      waitlistLastFocus.focus();
    }
    waitlistLastFocus = null;
  }

  // The <form> is emitted by Liquid's {% form %} tag, which does not reliably
  // pass a custom data- attribute through to the rendered markup. The modal
  // contains exactly one form, so select the element itself and stop depending
  // on an attribute that may never arrive.
  function waitlistForm(modal) {
    return modal.querySelector('[data-waitlist-form]') || modal.querySelector('form');
  }

  // Strip the box back to the four things worth reading once someone has
  // signed up: the eyebrow, a heading written for this moment, the
  // confirmation, and the footnote. The form and the invitation copy have done
  // their job and only get in the way.
  function showWaitlistSuccess(modal, message) {
    var form = waitlistForm(modal);
    var result = modal.querySelector('[data-waitlist-result]');
    var text = modal.querySelector('[data-waitlist-result-text]');
    var body = modal.querySelector('[data-waitlist-body]');
    var heading = modal.querySelector('#waitlist-heading');
    var error = modal.querySelector('[data-waitlist-error]');

    // Hide with an inline style, not the hidden attribute. The form carries a
    // `flex` class, and a class rule of display:flex beats the browser's own
    // display:none for [hidden] — so the attribute alone left the input, the
    // button and the server's own confirmation line still on screen. An inline
    // style outranks both.
    if (form) {
      form.hidden = true;
      form.style.display = 'none';
    }
    if (body) {
      body.hidden = true;
      body.style.display = 'none';
    }
    if (error) error.classList.add('hidden');

    if (heading && modal.dataset.successHeading) {
      heading.textContent = modal.dataset.successHeading;
    }

    if (text) text.textContent = message;
    if (result) result.hidden = false;

    storeSet('localStorage', WAITLIST_SIGNED_KEY, String(Date.now()));
  }

  // Let the confirmation stand for a moment, then fade it out on its own. The
  // modal already transitions its opacity over half a second, so closing it is
  // the fade. Any deliberate interaction cancels the timer — nobody should have
  // a box vanish while they are reaching for it.
  function autoDismissWaitlist(modal) {
    var wait = parseInt(modal.dataset.autoclose, 10);
    if (!wait || wait <= 0) return;

    var timer = window.setTimeout(closeWaitlist, wait);

    function cancel() {
      window.clearTimeout(timer);
    }

    modal.addEventListener('mouseenter', cancel, { once: true });
    modal.addEventListener('touchstart', cancel, { once: true });
    modal.addEventListener('focusin', cancel, { once: true });
  }

  function showWaitlistError(modal, message) {
    var slot = modal.querySelector('[data-waitlist-error]');
    if (!slot) return;
    slot.textContent = message;
    slot.classList.remove('hidden');
  }

  function initWaitlist() {
    var modal = document.getElementById('waitlist-modal');
    if (!modal || modal.dataset.waitlistBound === 'true') return;
    modal.dataset.waitlistBound = 'true';
    modal.dataset.open = 'false';

    var closeBtn = document.getElementById('close-modal');
    var backdrop = document.getElementById('waitlist-backdrop');
    if (closeBtn) closeBtn.addEventListener('click', closeWaitlist);
    if (backdrop) backdrop.addEventListener('click', closeWaitlist);

    document.addEventListener('keydown', function (event) {
      if (modal.dataset.open !== 'true') return;

      if (event.key === 'Escape') {
        closeWaitlist();
        return;
      }

      // Keep Tab inside the dialog while it is open.
      if (event.key === 'Tab') {
        var items = waitlistFocusable(modal);
        if (!items.length) return;
        var first = items[0];
        var last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });

    // Anything marked data-waitlist-open opens it — used by the Reserve buttons.
    // A visitor who has already signed up can still open it deliberately; only
    // the automatic pop-up is suppressed for them.
    document.querySelectorAll('[data-waitlist-open]').forEach(function (trigger) {
      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        openWaitlist();
      });
    });

    // These two exist only when the page was reloaded by a no-JS form submit.
    var succeeded = modal.querySelector('[data-server-status]');
    var failed = modal.querySelector('[data-server-error]');

    // Why the page reloads at all, rather than submitting in place:
    // Shopify protects customer forms with a captcha, and its own script submits
    // the form itself once the captcha clears. That submission cannot be
    // intercepted. Posting the form behind the scenes instead gets a 400 back
    // for having no captcha token, so the reload is Shopify's, not a choice.
    // What we can control is the arrival: reopen the modal immediately and show
    // the outcome, rather than leaving the visitor on a page that looks
    // unchanged.
    if (succeeded) {
      showWaitlistSuccess(modal, modal.dataset.success || '');

      // A beat before opening, so the page paints first and the box genuinely
      // fades in rather than appearing mid-render.
      window.setTimeout(function () {
        openWaitlist();
        autoDismissWaitlist(modal);
      }, 200);
      return;
    }

    if (failed) {
      openWaitlist();
      return;
    }

    var seen = Boolean(storeGet('sessionStorage', WAITLIST_SEEN_KEY));

    var delay = parseInt(modal.dataset.delay, 10);
    var inEditor = Boolean(window.Shopify && window.Shopify.designMode);

    // In the theme editor it always opens, so the section can be edited.
    if (inEditor) {
      if (delay > 0) window.setTimeout(openWaitlist, delay);
      return;
    }

    if (delay > 0 && !seen && !hasSignedUp()) {
      window.setTimeout(openWaitlist, delay);
    }
  }

  /* ---------------------------------------------------------------------
     Product gallery — carousel + thumbnails
     --------------------------------------------------------------------- */
  function initGallery(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-gallery]').forEach(function (gallery) {
      if (gallery.dataset.galleryBound === 'true') return;
      gallery.dataset.galleryBound = 'true';

      var track = gallery.querySelector('[data-gallery-track]');
      var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-gallery-thumb]'));
      var prev = gallery.querySelector('[data-gallery-prev]');
      var next = gallery.querySelector('[data-gallery-next]');
      if (!track) return;

      var index = 0;
      var count = track.children.length;

      function show(i) {
        index = Math.max(0, Math.min(i, count - 1));
        track.style.transform = 'translateX(-' + index * 100 + '%)';

        thumbs.forEach(function (thumb, position) {
          var active = position === index;
          thumb.classList.toggle('border-night', active);
          thumb.classList.toggle('border-transparent', !active);
          thumb.classList.toggle('opacity-100', active);
          thumb.classList.toggle('opacity-50', !active);
          thumb.setAttribute('aria-current', active ? 'true' : 'false');
        });

        if (prev) prev.disabled = index === 0;
        if (next) next.disabled = index === count - 1;
      }

      thumbs.forEach(function (thumb, position) {
        thumb.addEventListener('click', function () { show(position); });
      });
      if (prev) prev.addEventListener('click', function () { show(index - 1); });
      if (next) next.addEventListener('click', function () { show(index + 1); });

      gallery.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowLeft') show(index - 1);
        if (event.key === 'ArrowRight') show(index + 1);
      });

      // Swipe on touch devices — the original had none, and a gallery you can
      // only advance with a 64px chevron is painful on a phone.
      var startX = null;
      track.addEventListener('touchstart', function (e) { startX = e.touches[0].clientX; }, { passive: true });
      track.addEventListener('touchend', function (e) {
        if (startX === null) return;
        var delta = e.changedTouches[0].clientX - startX;
        if (Math.abs(delta) > 40) show(delta < 0 ? index + 1 : index - 1);
        startX = null;
      });

      // Variant changes tell the gallery which image to jump to.
      gallery.addEventListener('gallery:show', function (event) {
        if (typeof event.detail.index === 'number') show(event.detail.index);
      });

      show(0);
    });
  }

  /* ---------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------- */
  function init(root) {
    initAnnouncement();
    initHeader();
    initMobileMenu();
    initReveal(root);
    initCardFlip(root);
    initWaitlist();
    initGallery(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  document.addEventListener('shopify:section:select', function (event) { init(event.target); });

  window.ShadyTheme = { init: init, openWaitlist: openWaitlist, closeWaitlist: closeWaitlist };
})();
